import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  Heading,
  Text,
  Divider,
  Switch,
  Button,
  Link,
  SimpleGrid,
  useToast,
  Spinner,
} from '@chakra-ui/react';
// import { FaTrashAlt } from 'react-icons/fa'; // Removed react-icons import
import DashboardUserDetails from './DashboardUserDetails';
import { fetchUserProfile, updateUserProfile } from './services/api';

const DashboardAccount = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password] = useState('**********'); // Password is read-only/masked
  const [country, setCountry] = useState('');
  const [language, setLanguage] = useState('en');
  const [contactViaEmail, setContactViaEmail] = useState(false);
  const toast = useToast();

  const loadUserProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const profile = await fetchUserProfile();
      
      setFirstName(profile.firstName);
      setLastName(profile.lastName);
      setEmail(profile.email);
      setCountry(profile.countryCode);
      setLanguage(profile.locale);
      setContactViaEmail(profile.newsletter);
    } catch (error) {
      toast({
        title: 'Error loading profile',
        description: error.message || 'Failed to load user profile',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  const handleSaveChanges = async () => {
    try {
      await updateUserProfile({
        firstName,
        lastName,
        countryCode: country,
        locale: language,
        newsletter: contactViaEmail
      });

      toast({
        title: 'Profile updated',
        description: 'Your changes have been saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error saving changes',
        description: error.message || 'Failed to save profile changes',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteAccount = () => {
    // TODO: Implement delete account API call
    console.warn('Delete account requested');
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <Spinner size="xl" color="green.500" />
      </Flex>
    );
  }

  return (
    <Box p={8} bg="gray.50" borderRadius="lg">
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} position="relative">

        {/* Left Column: Account Info & Data/Privacy */}
        <VStack spacing={8} align="stretch">
          {/* Account Information and Access */}
          <Box>
            <Heading size="md" mb={2}>Account information and access</Heading>
            <Text fontSize="sm" color="gray.600" mb={4}>
              This is the personal information you use to access and manage your account.
              You can change your name, language and country settings.
            </Text>
            <DashboardUserDetails
              firstName={firstName}
              lastName={lastName}
              email={email}
              password={password}
              country={country}
              language={language}
              setFirstName={setFirstName}
              setLastName={setLastName}
              setCountry={setCountry}
              setLanguage={setLanguage}
            />
          </Box>

          <Divider />

          {/* Data and privacy settings */}
          <Box>
            <Heading size="md" mb={2}>Data and privacy settings</Heading>
            <Text fontSize="sm" color="gray.600" mb={6}>
              Control how your information is used and stored. Review your data preferences, update settings, and make choices that align with your privacy needs.
            </Text>
            <Flex justify="space-between" align="start">
              <Box>
                <Text fontWeight="bold">Delete Account</Text>
                <Text fontSize="sm" color="gray.600">
                  Request permanent deletion of your Calibrite account.
                </Text>
                <Text fontSize="sm" color="gray.600" fontStyle="italic">
                  This action will revoke access to Calibrite PROFILER and remove all associated user settings.
                </Text>
              </Box>
              <Button
                colorScheme="red"
                size="sm"
                onClick={handleDeleteAccount}
              >
                Delete
              </Button>
            </Flex>
          </Box>
        </VStack>

        {/* Right Column: Communication */}
        <VStack spacing={8} align="stretch">
          <Box>
            <Heading size="md" mb={2}>Communication</Heading>
            <Text fontSize="sm" color="gray.600" mb={6}>
              Choose how you'd like to hear from us. Manage your email subscriptions, product updates, and promotional messages—all in one place.
            </Text>
            <Flex justify="space-between" align="start">
              <Box>
                <Text fontWeight="bold">Contact me via e-mail</Text>
                <Text fontSize="sm" color="gray.600">
                  Keep me updated via email with helpful tips, educational resources, webinars, and occasional promotions about products and services.
                </Text>
                <Text fontSize="sm" color="gray.600">
                  See our <Link href="https://calibrite.com/privacy-policy" color="blue.500" isExternal>Privacy Policy</Link> for more details.
                </Text>
              </Box>
              <Switch
                colorScheme="green"
                isChecked={contactViaEmail}
                onChange={(e) => setContactViaEmail(e.target.checked)}
                size="lg" // Use lg for a size closer to the mockup
              />
            </Flex>
          </Box>
        </VStack>

        {/* Vertical Divider */}
        <Divider orientation="vertical" position="absolute" left="50%" top="0" bottom="0" borderColor="gray.300" display={{ base: 'none', lg: 'block' }} />

      </SimpleGrid>

      {/* Global Save Button */}
      <Flex justify="flex-end" mt={10}>
        <Button
          colorScheme="green"
          size="sm"
          onClick={handleSaveChanges}
        >
          Save
        </Button>
      </Flex>
    </Box>
  );
};

export default DashboardAccount; 